import { memo, type FC } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { EmptyState } from "../../../../components/EmptyState";
import type { CachedTopic } from "../../slice";

type TopicsSelectedTabProps = {
  selectedFilter: string;
  onSelectedFilterChange: (value: string) => void;
  onClearSelectedFilter: () => void;

  totalSelectedCount: number;
  filteredTopics: CachedTopic[];
  onRemoveTopic: (topicId: string) => void;

  showClearAll: boolean;
  onClearAll: () => void;
};

export const TopicsSelectedTab: FC<TopicsSelectedTabProps> = memo(
  ({
    selectedFilter,
    onSelectedFilterChange,
    onClearSelectedFilter,
    totalSelectedCount,
    filteredTopics,
    onRemoveTopic,
    showClearAll,
    onClearAll,
  }) => {
    return (
      <Box sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          id="intent-topics-selected-filter-input"
          placeholder="Filter selected topics..."
          size="small"
          fullWidth
          value={selectedFilter}
          onChange={(e) => onSelectedFilterChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: selectedFilter && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={onClearSelectedFilter}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {selectedFilter && (
          <Typography variant="caption" color="text.secondary">
            Showing {filteredTopics.length.toLocaleString()} of{" "}
            {totalSelectedCount.toLocaleString()} topics
          </Typography>
        )}

        {totalSelectedCount === 0 ? (
          <EmptyState
            icon={<SearchIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />}
            title="No topics selected yet"
            subtitle="Use the Search tab to find and add topics"
          />
        ) : (
          <Box sx={{ minHeight: 300 }}>
            {filteredTopics.length === 0 ? (
              <EmptyState
                icon={<SearchIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />}
                title={<>No topics match &quot;{selectedFilter}&quot;</>}
              />
            ) : (
              <Box
                sx={{ maxHeight: 300, overflow: "auto", overflowX: "hidden" }}
              >
                <Stack spacing={0}>
                  {filteredTopics.map((topic) => (
                    <Box
                      key={topic.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        mx: -2,
                        px: 2,
                        py: 0,
                        "&:hover": { bgcolor: "grey.50" },
                        "& .remove-button": {
                          opacity: 0,
                          transition: "opacity 0.15s ease",
                        },
                        "&:hover .remove-button, & .remove-button:focus-visible":
                          {
                            opacity: 1,
                          },
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Tooltip title={topic.displayName} enterDelay={500}>
                          <Typography variant="body2" noWrap>
                            {topic.displayName}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <IconButton
                        size="small"
                        className="remove-button"
                        aria-label={`Remove ${topic.displayName}`}
                        onClick={() => onRemoveTopic(topic.id)}
                        sx={{ ml: 1, flexShrink: 0 }}
                        disableRipple
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {showClearAll && (
          <Box sx={{ mt: 1, mx: -2, px: 2 }}>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<CloseIcon fontSize="small" />}
              onClick={onClearAll}
              fullWidth
              sx={{
                textTransform: "none",
                borderStyle: "dashed",
              }}
            >
              Clear all topics
            </Button>
          </Box>
        )}
      </Box>
    );
  }
);

TopicsSelectedTab.displayName = "TopicsSelectedTab";
